import { useEffect, useMemo, useState } from "react";

import brand, { getTenantSubdomain, type BrandConfig } from "@/config/brand";
import { api, authApi } from "@/lib/axios";
import { runSharedApiRequest } from "@/lib/apiRateLimit";
import { storage } from "@/utils/storage";

export type BrandingResponse = {
  name?: string;
  brand_name?: string;
  short_name?: string;
  slogan?: string;
  logo?: {
    light?: string;
    dark?: string;
    mark?: string;
    favicon?: string;
  };
  colors?: {
    primary?: string;
    secondary?: string;
  };
  login?: {
    hero_image?: string;
    tagline?: string;
  };
  support?: {
    email?: string;
    url?: string;
  };
  hide_brand_name?: boolean;
};

type UseBrandingOptions = {
  slug?: string | null;
};

export const useBranding = (options: UseBrandingOptions = {}) => {
  const configuredSlug =
    typeof import.meta.env.VITE_BRAND_SLUG === "string" &&
    import.meta.env.VITE_BRAND_SLUG.trim().length > 0
      ? import.meta.env.VITE_BRAND_SLUG.trim()
      : undefined;
  const slug = options.slug?.trim() || configuredSlug;
  const [branding, setBranding] = useState<BrandingResponse | null>(null);
  const [isLoadingBranding, setIsLoadingBranding] = useState(false);
  const [brandingError, setBrandingError] = useState<Error | null>(null);

  const tenantSubdomain = getTenantSubdomain();

  useEffect(() => {
    const token = storage.getItem("auth_token");
    let isMounted = true;

    const fetchBranding = async () => {
      setIsLoadingBranding(true);
      setBrandingError(null);
      try {
        let data: BrandingResponse | undefined;

        if (tenantSubdomain) {
          // Tenant subdomain — use the tenant branding endpoint (public, no auth needed)
          data = await runSharedApiRequest<BrandingResponse>(
            "branding:tenant",
            async () => {
              const response = await authApi.get<BrandingResponse>(
                "/organizations/tenant/branding/"
              );
              return response.data;
            },
            { rateLimitCooldownMs: 60_000 }
          );
        } else if (token) {
          try {
            data = await runSharedApiRequest<BrandingResponse>(
              "branding:current",
              async () => {
                const response = await api.get<BrandingResponse>(
                  "/organizations/organizations/branding/current/"
                );
                return response.data;
              },
              { rateLimitCooldownMs: 60_000 }
            );
          } catch {
            // If authenticated path fails, fall back to public slug resolver
            if (slug) {
              data = await runSharedApiRequest<BrandingResponse>(
                `branding:resolve:${slug}`,
                async () => {
                  const response = await authApi.get<BrandingResponse>(
                    "/organizations/organizations/branding/resolve",
                    {
                      params: { slug },
                    }
                  );
                  return response.data;
                },
                { rateLimitCooldownMs: 60_000 }
              );
            }
          }
        } else {
          if (slug) {
            data = await runSharedApiRequest<BrandingResponse>(
              `branding:resolve:${slug}`,
              async () => {
                const response = await authApi.get<BrandingResponse>(
                  "/organizations/organizations/branding/resolve",
                  {
                    params: { slug },
                  }
                );
                return response.data;
              },
              { rateLimitCooldownMs: 60_000 }
            );
          }
        }

        if (isMounted) {
          setBranding(data ?? null);
        }
      } catch (err) {
        if (isMounted) {
          setBrandingError(
            err instanceof Error ? err : new Error("Failed to load branding")
          );
        }
      } finally {
        if (isMounted) {
          setIsLoadingBranding(false);
        }
      }
    };

    fetchBranding();

    return () => {
      isMounted = false;
    };
  }, [slug, tenantSubdomain]);

  // Apply CSS variable overrides, page title, and favicon when branding loads
  useEffect(() => {
    if (!branding) return;

    if (branding.colors?.primary) {
      document.documentElement.style.setProperty(
        "--primary",
        hexToHsl(branding.colors.primary)
      );
      document.documentElement.style.setProperty(
        "--primary-foreground",
        getContrastForeground(branding.colors.primary)
      );
    }
    if (branding.colors?.secondary) {
      document.documentElement.style.setProperty(
        "--secondary",
        hexToHsl(branding.colors.secondary)
      );
      document.documentElement.style.setProperty(
        "--secondary-foreground",
        getContrastForeground(branding.colors.secondary)
      );
    }

    if (branding.brand_name) {
      document.title = branding.brand_name;
    }

    if (branding.logo?.favicon) {
      const link =
        document.querySelector<HTMLLinkElement>("link[rel~='icon']");
      if (link) link.href = branding.logo.favicon;
    }

    return () => {
      document.documentElement.style.removeProperty("--primary");
      document.documentElement.style.removeProperty("--primary-foreground");
      document.documentElement.style.removeProperty("--secondary");
      document.documentElement.style.removeProperty("--secondary-foreground");
    };
  }, [branding]);

  const resolvedBrand: BrandConfig = useMemo(
    () => ({
      ...brand,
      name: branding?.brand_name ?? brand.name,
      shortName: branding?.short_name ?? brand.shortName,
      slogan: branding?.slogan ?? brand.slogan,
      logo: {
        ...brand.logo,
        light: branding?.logo?.light ?? brand.logo.light,
        dark:
          branding?.logo?.dark ?? branding?.logo?.light ?? brand.logo.dark,
        square:
          branding?.logo?.mark ?? branding?.logo?.light ?? brand.logo.square,
      },
      login: {
        tagline: branding?.login?.tagline ?? brand.login?.tagline,
        heroImage: branding?.login?.hero_image ?? brand.login?.heroImage,
      },
      colors: branding?.colors,
      support: branding?.support,
      hideBrandName: branding?.hide_brand_name ?? false,
    }),
    [branding]
  );

  return {
    branding,
    resolvedBrand,
    isLoadingBranding,
    brandingError,
    isTenant: !!tenantSubdomain,
  };
};

function hexToHsl(hex: string): string {
  const r = parseInt(hex.slice(1, 3), 16) / 255;
  const g = parseInt(hex.slice(3, 5), 16) / 255;
  const b = parseInt(hex.slice(5, 7), 16) / 255;

  const max = Math.max(r, g, b);
  const min = Math.min(r, g, b);
  const l = (max + min) / 2;

  if (max === min) return `0 0% ${Math.round(l * 100)}%`;

  const d = max - min;
  const s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
  let h = 0;
  if (max === r) h = ((g - b) / d + (g < b ? 6 : 0)) / 6;
  else if (max === g) h = ((b - r) / d + 2) / 6;
  else h = ((r - g) / d + 4) / 6;

  return `${Math.round(h * 360)} ${Math.round(s * 100)}% ${Math.round(l * 100)}%`;
}

function getContrastForeground(hex: string): string {
  const r = parseInt(hex.slice(1, 3), 16);
  const g = parseInt(hex.slice(3, 5), 16);
  const b = parseInt(hex.slice(5, 7), 16);
  const background = relativeLuminance(r, g, b);
  const dark = relativeLuminance(15, 23, 42);
  const light = relativeLuminance(248, 250, 252);

  return contrastRatio(background, dark) >= contrastRatio(background, light)
    ? "222.2 47.4% 11.2%"
    : "210 40% 98%";
}

function relativeLuminance(r: number, g: number, b: number): number {
  const [red, green, blue] = [r, g, b].map((channel) => {
    const value = channel / 255;
    return value <= 0.03928
      ? value / 12.92
      : Math.pow((value + 0.055) / 1.055, 2.4);
  });

  return 0.2126 * red + 0.7152 * green + 0.0722 * blue;
}

function contrastRatio(a: number, b: number): number {
  return (Math.max(a, b) + 0.05) / (Math.min(a, b) + 0.05);
}
